import SelectComponent from "@via/components/select/select";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { SkeletonLoader } from "@via/components/loading/loading";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";
import { useVmetaProjects } from "@via/pages/projects/projects.api";
import {
  useVmetaCollections,
  useVmetaFields,
} from "@via/pages/projects/metadata/metadata.api";
import { requiredValidation } from "@via/utils/form-validators";
import type { SchedulerFormValues } from "@via/pages/runs/schedulers/utils/helpers";
import type { SelectOption } from "@via/utils/interfaces";
import { classNames } from "@via/utils/helpers";
import { useMemo, type JSX } from "react";
import { useWatch, type Control, type UseFormSetValue } from "react-hook-form";

export function SampleTrackerSourceFields({
  control,
  setValue,
  trackerView,
  setTrackerView,
  mongoUserId,
}: {
  control: Control<SchedulerFormValues>;
  setValue: UseFormSetValue<SchedulerFormValues>;
  trackerView: SampleTrackerViewType;
  setTrackerView: (v: SampleTrackerViewType) => void;
  mongoUserId?: string;
}): JSX.Element {
  const watchedSampleTrackerId = useWatch({ control, name: "sampleTrackerId" });
  const watchedCollectionId = useWatch({ control, name: "collectionId" });

  const { data: trackersResp, isLoading: isTrackersLoading } = useVmetaProjects(
    0,
    100,
    [{ id: "updatedAt", desc: true }],
    [],
    true,
    trackerView,
    mongoUserId
  );

  const trackerOptions = useMemo<SelectOption[]>(() => {
    const list = (trackersResp as any)?.data ?? [];
    return (Array.isArray(list) ? list : []).map((t: any) => ({
      label: t.label || t.name,
      value: t._id,
    }));
  }, [trackersResp]);

  const selectedTrackerId = watchedSampleTrackerId?.toString() ?? "";

  const { data: collectionsResp, isLoading: isCollectionsLoading } =
    useVmetaCollections(
      0,
      200,
      [{ id: "name", desc: false }],
      selectedTrackerId ? [{ id: "projectID", value: selectedTrackerId }] : []
    );

  const collectionOptions = useMemo<SelectOption[]>(() => {
    const list = (collectionsResp as any)?.data ?? [];
    return (Array.isArray(list) ? list : []).map((c: any) => ({
      label: c.label || c.name,
      value: c._id,
    }));
  }, [collectionsResp]);

  const selectedCollectionId = watchedCollectionId?.toString() ?? "";
  const { data: fieldsResp, isLoading: isFieldsLoading } = useVmetaFields(
    0,
    200,
    [{ id: "name", desc: false }],
    selectedCollectionId
      ? [{ id: "collectionID", value: selectedCollectionId }]
      : []
  );

  const fieldOptions = useMemo<SelectOption[]>(() => {
    const list = (fieldsResp as any)?.data ?? [];
    return (Array.isArray(list) ? list : []).map((f: any) => ({
      label: f.label || f.name,
      value: f.name,
    }));
  }, [fieldsResp]);

  const selectedFilterClass = "bg-indigo-600 text-white hover:bg-indigo-500";
  const unSelectedFilterClass = "bg-white";
  const defaultButtonClass =
    "relative inline-flex flex-none justify-center h-9 items-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap";

  return (
    <>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-900">
          Sample tracker source
        </label>
        <div className="mt-2 flex flex-col gap-3">
          <span className="isolate inline-flex rounded-md">
            <button
              type="button"
              onClick={() => setTrackerView(SampleTrackerViewType.My)}
              className={classNames(
                defaultButtonClass,
                "rounded-l-md min-w-[130px]",
                trackerView === SampleTrackerViewType.My
                  ? selectedFilterClass
                  : unSelectedFilterClass
              )}>
              My Trackers
            </button>
            <button
              type="button"
              onClick={() => setTrackerView(SampleTrackerViewType.Shared)}
              className={classNames(
                defaultButtonClass,
                "rounded-r-md -ml-px min-w-[150px]",
                trackerView === SampleTrackerViewType.Shared
                  ? selectedFilterClass
                  : unSelectedFilterClass
              )}>
              Shared with Me
            </button>
          </span>

          {isTrackersLoading ? (
            <SkeletonLoader count={1} />
          ) : (
            <SelectComponent
              control={control}
              name="sampleTrackerId"
              rules={{ required: "Sample tracker is required" }}
              options={trackerOptions}
              placeholder="Select a sample tracker"
              onChange={() => {
                setValue("collectionId", null);
                setValue("watchPathField", null);
                setValue("statusField", null);
              }}
            />
          )}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-900">
          Collection (belongs to tracker)
        </label>
        <div className="mt-2">
          {isCollectionsLoading && selectedTrackerId ? (
            <SkeletonLoader count={1} />
          ) : (
            <SelectComponent
              control={control}
              name="collectionId"
              rules={{ required: "Collection is required" }}
              options={collectionOptions}
              placeholder={
                selectedTrackerId
                  ? "Select a collection"
                  : "Select a tracker first"
              }
              readOnly={!selectedTrackerId}
              onChange={() => {
                setValue("watchPathField", null);
                setValue("statusField", null);
              }}
            />
          )}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-900">
          Source value field
        </label>
        <div className="mt-2">
          {isFieldsLoading && selectedCollectionId ? (
            <SkeletonLoader count={1} />
          ) : (
            <SelectComponent
              control={control}
              name="watchPathField"
              rules={{ required: "Source value field is required" }}
              options={fieldOptions}
              placeholder={
                selectedCollectionId
                  ? "Select a field"
                  : "Select a collection first"
              }
              readOnly={!selectedCollectionId}
            />
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Value from this field is the{" "}
          <span className="font-medium">source value</span> for each record (it
          can be a full folder path, run name, or any ID value; it does{" "}
          <span className="font-medium">not</span> need to be unique across
          records).
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Status field
          </label>
          <div className="mt-2">
            {isFieldsLoading && selectedCollectionId ? (
              <SkeletonLoader count={1} />
            ) : (
              <SelectComponent
                control={control}
                name="statusField"
                rules={{ required: "Status field is required" }}
                options={fieldOptions}
                placeholder={
                  selectedCollectionId
                    ? "Select a field"
                    : "Select a collection first"
                }
                readOnly={!selectedCollectionId}
              />
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Scheduler checks this field on each record and only submits runs
            when it matches the <span className="font-medium">Ready value</span>
            .
          </p>
        </div>

        <div>
          <FormInputComponent
            control={control}
            name="readyValue"
            label="Ready value"
            placeholder='e.g. "ready"'
            validations={requiredValidation("Ready value")}
          />
          <p className="mt-1 text-xs text-gray-500">
            A record is considered <span className="font-medium">ready</span>{" "}
            when <span className="font-medium">Status field</span> equals this
            value (string match). Only ready records are submitted.
          </p>
        </div>
      </div>
    </>
  );
}
